import React, { useState, useEffect } from 'react';
import BookListComponent from './BookListComponent';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
    setFirstRender,
    setSelectedClass,
} from '../../features/books/selectSlice';
import Select from '../common/Select';
import BookLibraryShimmer from '../common/shimmers/BookLibraryShimmer';
import SomethingWentWrong from '../../pages/errorPages/SomethingWentWrong';
import { trackEvent } from '../../segment';
import { NoInternetModal } from '../common/NoInternetModal';
import { setTeacherPendingActionsWidgetOpen } from '../../features/techerPendingActionsWidgetModal/teacherPendingActionsWidgetModalSlice';
import { setEvaluateOpen } from '../../features/Evaluate/EvaluateOpenSlice';
import { setLPOpen } from '../../features/LP/LPOpenSlice';
import { setTOCOpen } from '../../features/TOC/TOCOpenSlice';
import { setDQBOpen } from '../../features/DQB/DQBOpenSlice';
import { setLpPdfPlayerpen } from '../../features/LpPdfPlayer/LpPdfPlayerSlice';
import { setWidgetModalOpen } from '../../features/widgetModal/WidgetModalOpenSlice';
import { setEvaluateMediaHandler } from '../../features/EvaluatePdfPlayer/EvaluatePdfPlayerSlice';
import { setFullScreenModeEnabled } from '../../features/FullScreenMode/fullScreenMode';
import { useBookLibraryFetching } from '../../customHooks/useBookLibraryFetching';
import NeedHelpSection from '../help-section/NeedHelpSection';
import NeedHelpModal from '../help-section/NeedHelpModal';
import TeacherTraining from '../teacher-training/TeacherTraining';

const BookLibraryComponent: React.FC = () => {
    const dispatch = useDispatch();

    const {
        classSubjectsData: class_subjects_response,
        isClassSubjectsLoading,
        isBooksLoading,
        booksError,
        classOptions,
        fetchBooks,
        hasError,
    } = useBookLibraryFetching('book-library');

    const selectedClass = useSelector(
        (state: RootState) => state.selectState.selectedClass
    );
    const local_storage_drop_down_value = localStorage.getItem(
        'book-library-class-drop-down'
    );
    const isFirstLoad = useSelector(
        (state: RootState) => state.selectState.firstRender
    );
    const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
    const productType = useSelector((state: RootState) => state.product) as
        | 'Buildr'
        | 'ActiveTeach';
    const [showAllBooks, setShowAllBooks] = useState<boolean>(true);

    useEffect(() => {
        const initialTime = new Date();

        return () => {
            const timeSpentMilliseconds: number =
                new Date().getTime() - initialTime.getTime();

            // Convert milliseconds to seconds
            const timeSpentSeconds = timeSpentMilliseconds / 1000;
            trackEvent('home_page_time_spent', {
                time_spent_minutes: timeSpentSeconds / 60,
            });
            trackEvent('time_spent_book_library', {
                time_spent_sec: timeSpentSeconds,
            });
        };
    }, []);

    useEffect(() => {
        if (class_subjects_response?.success && classOptions.length > 0) {
            if (isFirstLoad) {
                const firstClassId = classOptions[0].value;
                dispatch(setSelectedClass(firstClassId));
                dispatch(setFirstRender(false));
            }
            if (!showAllBooks) {
                const initialClassId = classOptions[0]?.value;
                if (initialClassId) {
                    dispatch(setSelectedClass(initialClassId));
                }
            } else {
                setSelectedClasses(classOptions.map((option) => option.value));
            }
        }
    }, [
        class_subjects_response,
        classOptions,
        isFirstLoad,
        showAllBooks,
        dispatch,
    ]);

    useEffect(() => {
        if (!class_subjects_response) return;
        if (!showAllBooks && selectedClass) {
            fetchBooks(
                local_storage_drop_down_value
                    ? local_storage_drop_down_value
                    : selectedClass
            );
        } else if (showAllBooks && selectedClasses.length > 0) {
            if (
                local_storage_drop_down_value &&
                !local_storage_drop_down_value.startsWith('[')
            )
                fetchBooks(local_storage_drop_down_value);
            else fetchBooks(selectedClasses);
        }
    }, [
        selectedClass,
        selectedClasses,
        showAllBooks,
        class_subjects_response,
        fetchBooks,
        local_storage_drop_down_value,
    ]);

    const handleSelectChange = (
        value: string | string[],
        executed_from?: string
    ) => {
        if (!executed_from) {
            //drop down changed from book library
            trackEvent('book_library_class_filter_clicked', {});
            trackEvent('dropdown_class_book_library', {
                selected_class_id:
                    typeof value === 'string'
                        ? value != 'all'
                            ? value
                            : ''
                        : '',
            });
        }
        if (typeof value === 'string') {
            if (value !== 'all') setShowAllBooks(false);
            else {
                const filtered_val = classOptions
                    .filter((obj) => obj.value != 'all')
                    .map((obj) => obj.value);
                setShowAllBooks(true);
                localStorage.setItem(
                    'book-library-class-drop-down',
                    JSON.stringify(filtered_val)
                );
                fetchBooks(filtered_val);
                return;
            }
            dispatch(setSelectedClass(value));
            localStorage.setItem('book-library-class-drop-down', value);
            fetchBooks(value);
        }
    };

    useEffect(() => {
        if (!local_storage_drop_down_value) return;
        const last_selected_class = JSON.parse(
            localStorage.getItem('book-library-class-drop-down') || ''
        );
        handleSelectChange(last_selected_class, 'local_storage');
    }, []);

    const isOfflineModalOpen = useSelector(
        (state: RootState) => state.offlineModal.isOpen
    );

    useEffect(() => {
        dispatch(setEvaluateOpen(false));
        dispatch(setLPOpen(false));
        dispatch(setTOCOpen(false));
        dispatch(setDQBOpen(false));
        dispatch(setLpPdfPlayerpen(false));
        dispatch(setWidgetModalOpen(false));
        dispatch(setDQBOpen(false));
        dispatch(
            setEvaluateMediaHandler({
                isOpen: false,
                url: '',
                resource_type: '',
                resource_id: '',
                resource_title: '',
            })
        );
        const doc: any = document;
        if (
            document.fullscreenElement ||
            doc.webkitFullscreenElement ||
            doc.mozFullScreenElement ||
            doc.msFullscreenElement
        ) {
            doc.exitFullscreen?.();
            dispatch(setFullScreenModeEnabled(false));
        }
    }, [dispatch]);

    if (isClassSubjectsLoading) return <BookLibraryShimmer />;
    if (hasError) {
        return <SomethingWentWrong />;
    }

    return (
        <div
            className="pl-[2.5rem] pr-[4rem] py-[1rem] h-full w-full custom-scrollbar fixed"
            onClick={() => {
                dispatch(setTeacherPendingActionsWidgetOpen(false));
                return;
            }}
        >
            <div
                className={`flex gap-2 items-center text-black ${productType == 'Buildr' && 'justify-between'}`}
            >
                <h3 className="text-2xl">Book Library</h3>
                <div className="flex items-center mx-4">
                    {productType === 'ActiveTeach' && (
                        <Select
                            className="w-[10rem] rounded-full ml-4"
                            options={classOptions}
                            value={
                                local_storage_drop_down_value &&
                                !local_storage_drop_down_value.startsWith('[')
                                    ? local_storage_drop_down_value
                                    : selectedClass
                            }
                            onChange={handleSelectChange}
                            placeholder="Select Class"
                        />
                    )}
                    {productType === 'Buildr' && <NeedHelpModal />}
                </div>
            </div>

            {isBooksLoading ? (
                <BookLibraryShimmer />
            ) : booksError ? (
                <SomethingWentWrong />
            ) : (
                <>
                    {isOfflineModalOpen ? <NoInternetModal /> : <></>}

                    <div className="h-screen w-full overflow-y-scroll pb-32 custom-scrollbar ">
                        {<BookListComponent />}
                        {productType === 'Buildr' && <TeacherTraining />}
                    </div>
                </>
            )}
        </div>
    );
};

export default React.memo(BookLibraryComponent);
